import { useDebouncedState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useSellers } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { Item } from 'typings'
import { useFormStateContext } from '../../../providers'
import { MultiSelect } from '../../MultiSelect'
import { searchItems } from '../../utils'
import { useLoader } from './hooks/useLoader'
import { messages } from './messages'

type Props = {
  name: string
}

const MAIN_SELLER_ID = '1'
const MAX_SELLERS_DISPLAY = 8

export function SellersSelector(props: Props) {
  const { name } = props
  const { $t } = useIntl()
  const { form } = useFormStateContext()

  useLoader({ form, name })

  const [debouncedValue, setDebouncedValue] = useDebouncedState({
    initialState: '',
    timeoutMs: 500,
  })

  const items: Item[] = useWatch({ form, name })

  const helpText = useMemo(() => {
    const mainSellerIsSelected = Boolean(
      items?.find((s) => s.id === MAIN_SELLER_ID)
    )
    return mainSellerIsSelected ? $t(messages.mainSellerHelpText) : undefined
  }, [$t, items])

  const { data: sellers, isLoading: loadingOptions } = useSellers({
    options: { enabled: items !== undefined },
  })

  const filteredSellers = useMemo(() => {
    if (!sellers) {
      return []
    }
    if (debouncedValue) {
      return searchItems(sellers, debouncedValue).slice(0, MAX_SELLERS_DISPLAY)
    }

    return sellers.slice(0, MAX_SELLERS_DISPLAY)
  }, [debouncedValue, sellers])

  return (
    <MultiSelect
      options={filteredSellers}
      label={$t(messages.label)}
      name={name}
      loadingOptions={loadingOptions}
      onSearch={(text) => setDebouncedValue(text)}
      helpText={helpText}
    />
  )
}
